/**
 * Application root.
 *
 * Boot order matters: the WASM engine must be live before anything can be
 * parsed, and saved accounts decide whether the first screen says "add an
 * account" or opens the saved one. No mail-like UI is shown until both
 * facts are known; a failed bridge connection leaves the cached view up
 * with the error state in the shell.
 */
import type { JSX } from "preact";
import { useEffect } from "preact/hooks";

import { loadEngine } from "./engine/loader.ts";
import { listAccounts } from "./state/accounts.ts";
import { connectAccount } from "./state/session.ts";
import { connection, selectAccount } from "./state/store.ts";
import { Shell } from "./ui/shell.tsx";

/**
 * Renders the app, driving the boot sequence once.
 *
 * @returns The application element.
 */
export function App(): JSX.Element {
  useEffect(() => {
    // No cancellation guard on purpose: booting the engine and opening the
    // saved account is idempotent, and the signals outlive this component.
    void (async () => {
      try {
        await loadEngine();
        const summaries = await listAccounts();
        const first = summaries[0];
        if (first === undefined) {
          connection.value = "no-account";
          return;
        }
        selectAccount(first.id);
        connection.value = "connected";
        await connectAccount(first.id);
      } catch {
        // The engine or storage failed hard; the shell still renders so
        // Settings remains reachable.
        connection.value = "error";
      }
    })();
  }, []);

  if (connection.value === "booting") {
    return (
      <div class="boot-screen" role="status">
        <p>Loading the local mail engine…</p>
      </div>
    );
  }
  return <Shell />;
}
